import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GlassButton = ({ children, variant = 'primary', icon, onClick, className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer';
  const variants = {
    primary: 'glassmorphic-button text-white hover:scale-[1.02]',
    outline: 'bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.08]',
    danger: 'border border-red-500/30 text-red-400 hover:bg-red-500/10',
  };
  return (
    <button onClick={onClick} className={`${base} ${variants[variant]} ${className}`}>
      {icon && <Icon name={icon} size={15} />}
      {children}
    </button>
  );
};

const IntegrationsSection = () => {
  const [integrations] = useState([
    { id: 1, name: 'Dropbox', description: 'Sync your design files and renders automatically', icon: 'Cloud', connected: true, connectedDate: '11/20/2024', category: 'Storage' },
    { id: 2, name: 'Google Drive', description: 'Store and access your projects from Google Drive', icon: 'HardDrive', connected: false, category: 'Storage' },
    { id: 3, name: 'Slack', description: 'Get project notifications in your Slack workspace', icon: 'MessageSquare', connected: true, connectedDate: '10/15/2024', category: 'Communication' },
    { id: 4, name: 'Zapier', description: 'Connect with 3000+ apps and automate workflows', icon: 'Zap', connected: false, category: 'Automation' },
    { id: 5, name: 'AutoCAD', description: 'Import CAD files directly from AutoCAD', icon: 'Box', connected: true, connectedDate: '09/05/2024', category: 'Design Tools' },
    { id: 6, name: 'SketchUp', description: 'Import 3D models from SketchUp', icon: 'Layers', connected: false, category: 'Design Tools' },
  ]);

  const [apiKey] = useState('sk_live_51234567890abcdefghijklmnop');
  const [showApiKey, setShowApiKey] = useState(false);

  const handleConnect = (id) => { alert('Integration connection initiated. Please follow the authentication steps.'); };
  const handleDisconnect = (id) => { if (window.confirm('Are you sure you want to disconnect this integration?')) console.log(`Disconnecting: ${id}`); };
  const handleCopyApiKey = () => { navigator.clipboard?.writeText(apiKey); alert('API key copied to clipboard!'); };
  const handleRegenerateApiKey = () => { if (window.confirm('Are you sure? Your existing key will stop working immediately.')) alert('New API key generated!'); };

  const groupedIntegrations = integrations.reduce((acc, i) => { (acc[i.category] = acc[i.category] || []).push(i); return acc; }, {});

  return (
    <div className="space-y-6">
      {/* API Access */}
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
        <h2 className="text-xl font-bold text-white mb-1">API Access</h2>
        <p className="text-sm text-white/40 mb-6">Manage your API credentials for custom integrations</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">API Key</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showApiKey ? 'text' : 'password'} value={apiKey} readOnly
                  className="w-full px-4 py-3 pr-10 rounded-xl text-white text-sm bg-white/[0.05] border border-[rgba(254,131,56,0.15)] focus:outline-none"
                  style={{ backdropFilter: 'blur(10px)' }}
                />
                <button onClick={() => setShowApiKey(!showApiKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                  <Icon name={showApiKey ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>
              <GlassButton variant="outline" icon="Copy" onClick={handleCopyApiKey}>Copy</GlassButton>
            </div>
            <p className="text-xs text-white/30 mt-2">Keep your API key secure. Do not share it publicly or commit it to version control.</p>
          </div>

          <div className="flex gap-3">
            <GlassButton variant="danger" icon="RefreshCw" onClick={handleRegenerateApiKey}>Regenerate Key</GlassButton>
            <GlassButton variant="outline" icon="ExternalLink">View Documentation</GlassButton>
          </div>
        </div>
      </div>

      {/* Integration Groups */}
      {Object.entries(groupedIntegrations).map(([category, items]) => (
        <div key={category} className="glassmorphic-card rounded-2xl p-6 md:p-8">
          <h2 className="text-lg font-bold text-white mb-4">{category}</h2>
          <div className="space-y-3">
            {items.map((integration) => (
              <div key={integration.id} className="flex items-start justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(254, 131, 56, 0.15), rgba(236, 103, 30, 0.08))', border: '1px solid rgba(254, 131, 56, 0.2)' }}>
                    <Icon name={integration.icon} size={20} className="text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-white">{integration.name}</h3>
                      {integration.connected && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-emerald-400" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>Connected</span>
                      )}
                    </div>
                    <p className="text-xs text-white/40">{integration.description}</p>
                    {integration.connected && integration.connectedDate && (
                      <p className="text-[10px] text-white/25 mt-1">Connected on {integration.connectedDate}</p>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  {integration.connected ? (
                    <GlassButton variant="outline" icon="Unplug" onClick={() => handleDisconnect(integration.id)}>Disconnect</GlassButton>
                  ) : (
                    <GlassButton icon="Link" onClick={() => handleConnect(integration.id)}>Connect</GlassButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Request Integration */}
      <div className="glassmorphic-card rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-orange-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-white mb-1">Need More Integrations?</h3>
            <p className="text-xs text-white/40 mb-3">We're constantly adding new integrations. If you need a specific integration, let us know.</p>
            <GlassButton variant="outline" icon="Mail">Request Integration</GlassButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSection;
